import { setLabel } from './i18n';
import {
  CityTime,
  dialMinutes,
  formatCityTime,
  handAngles,
  type CityTimeSnapshot,
} from '../time/cityTime';
import { createElement } from './dom';
import { createIcon } from './icons';

const NS = 'http://www.w3.org/2000/svg';
const NUMERALS = ['XII', 'I', 'II', 'III', 'IV', 'V', 'VI', 'VII', 'VIII', 'IX', 'X', 'XI'];
function svgElement(
  tag: string,
  attributes: Record<string, string>,
  parent: SVGElement
): SVGElement {
  const element = document.createElementNS(NS, tag);
  for (const [name, value] of Object.entries(attributes)) element.setAttribute(name, value);
  parent.append(element);
  return element;
}

function clockFace(parent: HTMLElement) {
  const svg = document.createElementNS(NS, 'svg');
  svg.setAttribute('viewBox', '0 0 112 112');
  svg.setAttribute('aria-hidden', 'true');
  svgElement('circle', { cx: '56', cy: '56', r: '53', class: 'time-clock-rim' }, svg);
  svgElement('circle', { cx: '56', cy: '56', r: '32', class: 'time-clock-inner' }, svg);
  for (let i = 0; i < 12; i++) {
    const angle = (i * Math.PI) / 6;
    const text = svgElement(
      'text',
      { x: String(56 + Math.sin(angle) * 42), y: String(56 - Math.cos(angle) * 42 + 4) },
      svg
    );
    text.textContent = NUMERALS[i] ?? '';
    svgElement(
      'path',
      { d: 'M56 25 V28', transform: `rotate(${i * 30} 56 56)`, class: 'time-clock-tick' },
      svg
    );
  }
  const hour = svgElement('g', {}, svg);
  svgElement(
    'path',
    {
      d: 'M54.3 62 L54.3 44 L51.8 44 L56 34 L60.2 44 L57.7 44 L57.7 62 Z',
      class: 'time-clock-hand',
    },
    hour
  );
  const minute = svgElement('g', {}, svg);
  svgElement(
    'path',
    { d: 'M55.1 65 L55.1 31 L56 24.5 L56.9 31 L56.9 65 Z', class: 'time-clock-minute' },
    minute
  );
  svgElement('circle', { cx: '56', cy: '56', r: '3', class: 'time-clock-pivot' }, svg);
  parent.append(svg);
  return { hour, minute };
}

/**
 * A period-inspired dial; the control is not a replica of a documented clock. It has a minute hand
 * because users asked to read the minutes; the hour hand is still the one that is dragged.
 */
export function createTimeClock(parent: HTMLElement, time: CityTime) {
  const root = createElement('div', 'time-clock', parent);
  const dial = createElement('div', 'time-clock-dial', root);
  dial.setAttribute('role', 'slider');
  for (const [name, value] of Object.entries({ 'aria-valuemin': '0', 'aria-valuemax': '1439' }))
    dial.setAttribute(name, value);
  const hands = clockFace(dial);
  const reset = createElement('button', 'painting-minimap-button time-clock-reset', root);
  reset.type = 'button';
  setLabel(reset, 'title', 'Return to live city time');
  setLabel(reset, 'aria-label', 'Return to live city time');
  reset.append(createIcon('restart', 10));
  reset.addEventListener('click', (event) => {
    if (event.detail > 0) reset.blur();
    time.reset();
    update(time.snapshot());
  });
  bindDial(dial, time, () => {
    update(time.snapshot());
  });
  root.addEventListener('keydown', (event) => {
    if (event.code === 'Escape' && event.target instanceof HTMLElement) event.target.blur();
    if (event.code !== 'KeyM' && event.code !== 'Tab') event.stopPropagation();
  });
  let last = '';
  function update(value: CityTimeSnapshot): void {
    const text = formatCityTime(value.minutes),
      key = `${text}:${value.live}:${time.shared}:${time.editable}`;
    if (key === last) return;
    last = key;
    setLabel(
      dial,
      'title',
      `${text} · ${time.editable ? 'Drag to change time' : 'Shared city time'}`
    );
    updateDialAccessibility(dial, time, value, text);
    const angles = handAngles(value.minutes);
    hands.hour.setAttribute('transform', `rotate(${angles.hour} 56 56)`);
    hands.minute.setAttribute('transform', `rotate(${angles.minute} 56 56)`);
    root.dataset.live = String(value.live);
    root.dataset.minutes = String(Math.floor(value.minutes));
    reset.disabled = value.live;
    reset.hidden = !time.editable || value.live;
  }
  update(time.snapshot());
  return { update };
}

function bindDial(dial: HTMLElement, time: CityTime, update: () => void): void {
  let pointer: number | null = null;
  const select = (event: PointerEvent) => {
    const box = dial.getBoundingClientRect(),
      x = event.clientX - box.x - box.width / 2,
      y = event.clientY - box.y - box.height / 2;
    if (Math.hypot(x, y) < box.width * 0.13) return;
    time.set(dialMinutes(x, y, time.snapshot().minutes));
    update();
  };
  dial.addEventListener('pointerdown', (event) => {
    event.preventDefault();
    if (!time.editable) return;
    if (event.button !== 0 || pointer !== null) return;
    if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
    pointer = event.pointerId;
    dial.setPointerCapture(pointer);
    select(event);
  });
  dial.addEventListener('pointermove', (event) => {
    if (pointer === event.pointerId) select(event);
  });
  dial.addEventListener('pointerup', (event) => {
    if (pointer !== event.pointerId) return;
    if (dial.hasPointerCapture(pointer)) dial.releasePointerCapture(pointer);
    pointer = null;
  });
  for (const type of ['pointercancel', 'lostpointercapture'])
    dial.addEventListener(type, () => {
      pointer = null;
    });
  dial.addEventListener('keydown', (event) => {
    if (!time.editable) return;
    const steps: Record<string, number> = {
      ArrowRight: 5,
      ArrowUp: 5,
      ArrowLeft: -5,
      ArrowDown: -5,
      PageUp: 60,
      PageDown: -60,
    };
    const step = steps[event.code];
    if (step !== undefined) time.set(time.snapshot().minutes + step);
    else if (event.code === 'Home') time.reset();
    else if (event.code === 'End') time.set(0);
    else return;
    event.preventDefault();
    update();
  });
}

function updateDialAccessibility(
  dial: HTMLElement,
  time: CityTime,
  value: CityTimeSnapshot,
  text: string
): void {
  dial.setAttribute('aria-disabled', String(!time.editable));
  setLabel(dial, 'aria-label', time.editable ? 'Change city time' : 'Shared city time');
  dial.tabIndex = time.editable ? 0 : -1;
  dial.setAttribute('aria-valuenow', String(Math.floor(value.minutes)));
  setLabel(
    dial,
    'aria-valuetext',
    `${text}, ${time.shared ? 'shared city time' : value.live ? 'current Amsterdam time' : 'selected time'}`
  );
}
